


import { useState, useEffect, useRef } from 'react';
import Editor from "@monaco-editor/react";
import { Grid, Typography, MenuItem, MenuList, InputBase, Button, Menu, Paper, IconButton, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import Requester from '../../store/blueprint/svc';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';
import LoaderWithProgress from '../atoms/loader/LoaderWithProgress';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Tooltip from '@material-ui/core/Tooltip';

// Adds a global listener to suppress Monaco editor's expected "Canceled" promise errors
// Ensures the listener is added only once even during hot reloads to avoid duplicate handling
if (typeof window !== "undefined" && !window.__monacoCancelHandled) {
    window.addEventListener("unhandledrejection", (event) => {
      const reason = event?.reason;
  
      // Suppress Monaco’s expected "Canceled" errors only
      if (reason && (reason.name === "Canceled" || reason.message === "Canceled")) {
        event.preventDefault(); // stop the noisy console error
      }
    });
  
    // mark it so we don’t re-add the listener on hot reload
    window.__monacoCancelHandled = true;
}

const fetch = new Requester();
const useStyles = makeStyles((theme) => ({
    search: {
        width: '100%',
        position: 'relative',
        borderRadius: theme.shape.borderRadius,
        border: '1px solid #ccc'
    },
    filterDiv: {
        fontColour: '#ccc',
        height: '100%',
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        right: 0, // Position the search icon at the right
    },
    inputRoot: {
        color: 'inherit',
        maxWidth: 'calc(100% - 60px)'
    },
    inputInput: {
        padding: theme.spacing(1, 1, 1, 0),
        paddingLeft: `calc(1em)`,
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            width: '20ch',
            '&:focus': {
                width: '25ch',
            },
        },
        // Add flex properties to make it expand and fill available space
        display: 'flex',
        flexGrow: 1,
    },
    noResult: {
        margin: '10px 12px',
        fontColour: '#ccc',
        fontWeight: 400
    },
    filterBtn: {
        fontSize: 14,
        fontWeight: 600,
        textTransform: 'none',
        color:  '#737373',
        focusRipple: 'none'
    },
    dropdownMenu: {
        marginTop: "34px",
    },
    dropdownMenuPaper: {
        borderRadius: "4px",
        border: "solid 1px #ccc",
        boxShadow: 'none'
    },
    fileListContainer: {
        overflowY: 'scroll',
        width:"100%"
        
    },
    radioContainer: {
        width: '100%',
        margin: '10px 0'
    }
}));

export default function BuildOutput() {
    const classes = useStyles();
    const editorRef = useRef(null);
    const searchDisposableRef = useRef(null);
    
    const [fileType, setFileType] = useState('ruby');
    const [files, setFiles] = useState([]);
    const [filteredFiles, setFilteredFiles] = useState([]);
    const [activeFile, setActiveFile] = useState(null);
    const [fileContent, setFileContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [contentLoading, setContentLoading] = useState(false);
    const [searchedQuery, setSearchedQuery] = useState('');
    const [selectedFilter, setSelectedFilter] = useState("name");
    const currentUrl = window.location.href;
    const [slug, blueprintID, tag, transactionId] = currentUrl.split('/').slice(4);
    const filterOptions = [{ label: "Search in files", value: "name", name: 'Files' }, { label: "Search in content", value: "content", name: 'Content' }];
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [showNoBuildFoundAlert, setShowNoBuildAlert] = useState(false);
    const [monacoInstance, setMonacoInstance] = useState(null);


    let hasSuppressedMonacoCancel = false;

    // Called when the Monaco editor mounts; stores references to editor and monaco instance
    // Also suppresses "Canceled" unhandled promise rejections from Monaco to prevent console noise
    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        setMonacoInstance(monaco);

        if (!hasSuppressedMonacoCancel) {
            window.addEventListener("unhandledrejection", (event) => {
            if (event?.reason?.message === "Canceled" || event?.reason?.name === "Canceled") {
                event.preventDefault();
            }
            });
            hasSuppressedMonacoCancel = true;
        }
    };
        
    // Safely cancels any active Monaco editor search, closes find widget, and clears timers
    // Ensures no residual search highlights or errors remain before model switches or unmount
    const cancelMonacoSearchSafely = () => {
        try {
          if (editorRef.current) {
            const findController = editorRef.current.getContribution('editor.contrib.findController');
            if (findController?.getState()?.isRevealed) {
              // Close find widget before model switch
              findController.closeFindWidget();
            }
      
            // Dispose pending search results if any
            disposeEditorSearchRef();
          }
      
          // Also clear any global timers
          if (window.__searchTimer) clearTimeout(window.__searchTimer);
        } catch (err) {
          if (err?.message !== "Canceled") console.warn("Search cleanup error:", err);
        }
      };
    

    const languages = { rb: 'ruby' };

    const toggleSidebar = () => {
        setIsCollapsed(prev => !prev);
    };

    const fetchSelectedFileContent = async (activeFile) => {
        try {
           
            if(activeFile === null || activeFile === undefined) return;
            setContentLoading(true);
            let response = await fetch.post(fetch.route.getBuildFileContent, {blueprintID, filePath: activeFile?.filePath});
            if(response.status){
                updateEditorContent({name: activeFile?.name, content: response?.data?.content || ''})
                setContentLoading(false);

                if (selectedFilter === "content" && searchedQuery) {
                    setTimeout(() => {
                      try {
                        if (editorRef.current && editorRef.current.getModel()) {
                          triggerEditorSearch(searchedQuery);
                        }
                      } catch (err) {
                        if (err?.message !== 'Canceled') console.error(err);
                      }
                    }, 250);
                }
    
            }else{
                throw new Error('Content not found');
            }
        } catch (error) {
            console.error(`Loading file content failed. ${error}`);
            setFileContent('');
        }
    }

    const handleActiveFileChange = (file) => {
        if (!file) return;
        cancelMonacoSearchSafely();
        setContentLoading(true);

        setTimeout(() => {
            setActiveFile(file);
            fetchSelectedFileContent(file);
        }, 100);


    }

    const updateEditorContent = (file) => {
        try {
            let type = file.name.split('.').pop();
            if (languages[type] !== undefined)
                setFileType(languages[type]);
            else
                setFileType(type);

            if(type === "json") {
                const res = JSON.parse(file?.content);
                file.content = JSON.stringify(res, null, 2);
            }

            setFileContent(file?.content);

        } catch (error) {
            console.error(`Setting file content failed. ${error}`);
            setFileContent('');
        }
    }

    
    const handleFilterClick = (option) => {

        setSelectedFilter(option.value);
        
        if(option.value === "name"){
            filterFilesList(searchedQuery, option.value)
        }else{
            fetchBuildOutputData(searchedQuery);
        }
    }

    

    const fetchBuildOutputData = async (search=null) => {
        try {
            if(search !== null){
                triggerEditorSearch(search);

                const payload = { blueprintId: blueprintID, searchedQuery: search, }
                let response = await fetch.post(fetch.route.getBuildFiles, payload);
                if(response.status){
                    setFiles(response?.data?.blueprintFiles || []);
                    setFilteredFiles(response?.data?.blueprintFiles || [])
                    
                    const active = response?.data?.activeFile;
                    if(active){
                        setActiveFile(active);
                        updateEditorContent(active);
                    }else{
                        setFileContent('');
                        setActiveFile(null);
                    }

                    
                }else{
                    throw new Error(`Something went wrong while fetching search result`);
                }
                return;
            }

            setLoading(true);
            const payload = {  blueprintId: blueprintID,  searchedQuery: search,  }
            let response = await fetch.post(fetch.route.getBuildFiles, payload);
            if(response.status){
                if(response?.data == null ){
                    setShowNoBuildAlert(true);
                    setLoading(false);
                    return;
                }

                setFiles(response?.data?.blueprintFiles || []);
                setFilteredFiles(response?.data?.blueprintFiles || [])

                const active = response?.data?.activeFile 
                if(active){
                    setActiveFile(active);
                    updateEditorContent(active);
                }

                setLoading(false);
            }else{
                throw new Error(`Something went wrong`);
            }


        } catch (error) {
            console.error(`Fetching build files failed. ${error}`);
        }
    }
    
    // 🔍 Triggers a search in the Monaco editor using the given query string  
    // and automatically scrolls to highlight the first matched result.
    const triggerEditorSearch = (query) => {
        try {
          if (!editorRef.current || !monacoInstance) return;
      
          const editor = editorRef.current;
      
          // 🔹 Cancel previous highlights before new search
          if (searchDisposableRef.current) {
            searchDisposableRef.current.dispose();
            searchDisposableRef.current = null;
          }
      
          // Access the find controller
          const findController = editor.getContribution("editor.contrib.findController");
          const findState = findController.getState();
      
          // Open find widget if needed
          if (!findState.isRevealed) {
            editor.getAction("actions.find").run();
          }
      
          // Update search state
          findState.change(
            {
              searchString: query,
              isRevealed: true,
            },
            false
          );
      
          // 🔹 Scroll to the first match after a short delay (to allow Monaco to render highlights)
          setTimeout(() => {
            const model = editor.getModel();
            if (!model) return;
            const matches = model.findMatches(query, false, false, false, null, true);
            if (matches.length > 0) {
              // Scroll editor to first match
              editor.revealRangeInCenter(matches[0].range);
              editor.setSelection(matches[0].range); // optional: highlight the match
            }
          }, 200);
      
          // Keep your search input focused
          setTimeout(() => {
            const input = document.getElementById("searchField");
            if (input) input.focus();
          }, 0);
        } catch (error) {
          console.error(`Applying search in editor failed. ${error}`);
          return null;
        }
      };
      
    const filterFilesList = (query, filter) => {
        const regex = new RegExp(query, "i");
        const matchedArray = files.filter(file => regex.test(file?.name));
        setFilteredFiles(matchedArray);
    }

    const handleSearchInputChange = (event) => {
        const query = event.target.value;
        setSearchedQuery(query);

        if(selectedFilter === "name"){
            filterFilesList(event.target.value, "name");
        }else{
            fetchBuildOutputData(query);
        }
    }
   
    const activeOne = { color: 'red' }

    // Cleans up any active Monaco editor search references to prevent memory leaks
    const disposeEditorSearchRef = () => {
        try {
            if (searchDisposableRef.current) {
                searchDisposableRef.current.dispose();
                searchDisposableRef.current = null;
            }
        } catch (err) {
            if (err?.message !== "Canceled") console.warn("Search disposal error:", err);
        }
    }


    // Safely disposes the Monaco editor instance when the component unmounts
    // ensuring any pending searches or resources are properly cleared
    const handleEditorWillUnmount = () => {
        try {
          cancelMonacoSearchSafely();
          editorRef.current?.dispose?.();
        } catch (err) {
          if (err?.message !== "Canceled") console.warn("Editor dispose error:", err);
        }
    };

    // 🛡️ Sets up a global handler to suppress unhandled promise rejections  
    // specifically caused by Monaco editor "Canceled" errors to avoid console noise.
    useEffect(() => {
        const handleRejection = (event) => {
            const reason = event?.reason;
            if (reason && (reason.name === "Canceled" || reason.message === "Canceled")) {
              // Prevent the noisy console error from Monaco disposals
              event.preventDefault();
            }
          };
        
          window.addEventListener("unhandledrejection", handleRejection);

        return () => {
            window.removeEventListener("unhandledrejection", handleRejection);
        };

    }, []);

    


    useEffect(() => {
        fetchBuildOutputData();
    }, []);
    
    return (
        <Grid className="buildOutputContainer" container style={{height: '60vh', padding:0, margin:0}} direction={"row"} spacing={0} >
            {loading ?
            <Grid item xs={12} style={{ padding: 15 ,minHeight:"400px", display:"flex", justifyContent:"center", alignItems:"center"}} >
                <LoaderWithProgress size={12} isContent={true} isLoader={true}/>
            </Grid> : 
            <>
                {showNoBuildFoundAlert ? 
                <Grid item xs={12} style={{minHeight:"500px", padding: '24px' }}>
                    <Alert style={{padding: '24px'}} severity="warning">No build files found. Please build the blueprint first.</Alert>
                </Grid>
                :
            
                <>
                    {!isCollapsed && 
                    <Grid className="leftPanel" item xs={3} style={{ height: '100%', transition: "width 0.3s ease" }}>
                        <Paper
                            elevation={3}
                            style={{
                                height: '100%',
                                width: "100%",
                                background: "white",
                                zIndex: 10,
                                paddingRight: "14px",
                                boxShadow: "2px 0px 5px rgba(0,0,0,0.2)",
                                position: "relative", // Needed for absolute positioning of the button
                                display: "flex",
                                flexDirection: "column",
                                alignItems: isCollapsed ? "center" : "flex-start",
                            }}
                        >
                            <div className="filter_container">
                                <div className={classes.search}>
                                    <InputBase
                                        placeholder="Search"
                                        classes={{
                                            root: classes.inputRoot,
                                            input: classes.inputInput,
                                        }}
                                        onChange={handleSearchInputChange}
                                        inputProps={{ 'aria-label': 'search' }}
                                        id="searchField"
                                    />
                                </div>
                                <div className={classes.radioContainer}>
                                    <RadioGroup
                                            row  
                                            value={selectedFilter}
                                            onChange={(e) => {
                                                const option = filterOptions.find(opt => opt.value === e.target.value);
                                                handleFilterClick(option);
                                            }}
                                            style={{display: "flex", flexDirection: 'row', flexWrap: 'nowrap'}}
                                            >
                                            {filterOptions.map((option) => (
                                                <FormControlLabel
                                                    key={option.value}
                                                    value={option.value}
                                                    control={<Radio
                                                        sx={{
                                                            p: 0.5, // reduce padding around radio
                                                            '& .MuiSvgIcon-root': {
                                                              fontSize: 18, // default ~24px → smaller
                                                            },
                                                          }}    
                                                    />}
                                                    label={option.label}
                                                    sx={{
                                                        mx: 0.5, // reduce horizontal margin
                                                        "& .MuiFormControlLabel-label": {
                                                        fontSize: "12px",
                                                        },
                                                        marginRight: "4px",
                                                    }}
                                                />
                                            ))}
                                    </RadioGroup>
                                </div>
                            </div>

                            <div className={classes.fileListContainer}>
                            {filteredFiles.length > 0 ?                            
                                <MenuList id="filesList">
                                    {filteredFiles.map((file, index) => <MenuItem key={index} className={`file-item ${file?.name === activeFile?.name && "active-file-item"}`} style={file?.name === activeFile?.name ? activeOne : {}} onClick={e => handleActiveFileChange(file)}>{file?.name}</MenuItem>)}
                                </MenuList>
                                :
                                <Typography className={classes.noResult}> No result found </Typography>                            
                            }
                            </div>

                        </Paper>                        
                    </Grid>}

                    <Grid className="rightPanel" item xs={isCollapsed ? 12 : 9} style={{ height: '100%', transition: "width 0.3s ease", position: 'relative' }}>
                    
                        <Tooltip title={isCollapsed ? "Expand Drawer" : "Collapse Drawer"} arrow>
                            <IconButton
                                id={isCollapsed ? "expand-button" : "collapse-button"}
                                onClick={toggleSidebar}
                                style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: `${isCollapsed ? "-2px" : "-15px"}`, // Left border of logContainer
                                    transform: "translateY(-50%)",
                                    border: "1px solid #ccc",
                                    boxShadow: "2px 2px 5px rgba(0,0,0,0.2)",
                                    zIndex: 2000,
                                    background: "#e0e0e0", // Light gray background
                                    width: "24px", // Smaller size
                                    height: "24px",
                                    minWidth: "unset"
                                }}
                            >
                               {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />} 
                            </IconButton>
                        </Tooltip>

                        {contentLoading ? 
                            <LoaderWithProgress size={12} styleRoot={{height: '100%', width: '100%'}} isContent={true} isLoader={true}/>
                         : 
                         <>
                            <Editor
                                height="60vh"
                                defaultLanguage="lua"
                                language={fileType}
                                theme="vs-dark"
                                path={activeFile?.name}
                                value={fileContent}
                                loading="Loading content.."
                                onMount={handleEditorDidMount}
                                beforeMount={(monaco) => setMonacoInstance(monaco)}
                                onUnmount={handleEditorWillUnmount}

                            />    
                         </>
                        }
                    </Grid>
                </>

                }
            </>}
        </Grid>
    )
}